<template>
  <bm-dialog
    :visible="isVisible"
    :label="dialogTitle"
    :width="dialogOptions.width"
    :dialog-type="dialogOptions.type"
    :closeable="dialogOptions.closeable"
    :large="dialogOptions.large"
    @accept="submit(true)"
    @cancel="submit(false)"
  >
    <v-row>
      <v-col cols="12">
        <span
          v-if="dialogOptions.isBodyHtml"
          class="pre-wrap"
          v-html="dialogMessage"
        />
        <span v-else class="pre-wrap">{{ dialogMessage }}</span>
      </v-col>
    </v-row>

    <template #controls="props">
      <bm-dialog-controls
        v-bind="props"
        :cancelable="true"
        :large="dialogOptions.large"
        data-qa="confirmation-dialog-controls"
      >
        <template #controls-confirm-text>
          {{ dialogOptions.customConfirmButtonText || $m.ok }}
        </template>
        <template #controls-cancel-text>
          {{ dialogOptions.customCancelButtonText || $m.cancel }}
        </template>
      </bm-dialog-controls>
    </template>
  </bm-dialog>
</template>

<script lang="ts">
import BmDialog from "@/components/dialogs/BmDialog.vue";
import BmDialogControls from "@/components/dialogs/BmDialogControls.vue";
import { computed, defineComponent, ref } from "vue";
import type { BmConfirmationDialogOptions } from "@/components/dialogs/DialogTypes";
import { EnumDialogType } from "@/components/dialogs/DialogTypes";

const defaultOptions: BmConfirmationDialogOptions = {
  type: EnumDialogType.none,
  width: 350,
  customConfirmButtonText: "",
  customCancelButtonText: "",
  closeable: true,
  isBodyHtml: false,
  large: false,
};

export default defineComponent({
  name: "BmConfirmationDialog",
  components: { BmDialog, BmDialogControls },
  props: {},
  setup(props, context) {
    const dialogTitle = ref("");
    const dialogMessage = ref("");
    const dialogOptions = ref(defaultOptions);
    const returnPromise = ref<Promise<boolean>>();
    const isVisible = computed(() => returnPromise.value !== undefined);

    let resolver: (value?: PromiseLike<boolean> | boolean) => void;

    /**
     * This is rather oddly designed to be called externally.
     * The returned promise will resolve when the dialog is confirmed/cancelled.
     */
    const open = (
      title: string,
      message: string,
      options?: Partial<BmConfirmationDialogOptions>
    ): Promise<boolean> => {
      dialogTitle.value = title;
      dialogMessage.value = message;

      // Merge them into a blank obj as to prevent our default options being overwritten.
      dialogOptions.value = Object.assign({}, defaultOptions, options);

      returnPromise.value = new Promise<boolean>((resolve) => {
        resolver = (value?: PromiseLike<boolean> | boolean) => {
          resolve(value ?? false); // Assume we don't want to do the action if undefined
          returnPromise.value = undefined;
        };
      });
      return returnPromise.value;
    };

    const submit = (state: boolean) => {
      resolver(state);
    };

    return {
      open,
      submit,
      dialogTitle,
      dialogMessage,
      dialogOptions,
      isVisible,
    };
  },
});
</script>
<style lang="sass" scoped>
.pre-wrap
  white-space: pre-wrap
</style>
